import React from 'react';
import { IdvScreen } from '../screens/IdvScreen.js';
import { CustomerSearchScreen } from '../screens/CustomerSearchScreen.js';
import { MediumSecurityScreen } from '../screens/MediumSecurityScreen.js';
import { OutboundSecurityScreen } from '../screens/OutboundSecurityScreen.js';
import { EclMediumSecurityScreen } from '../screens/EclMediumSecurityScreen.js';
import { EclHighSecurityScreen } from '../screens/EclHighSecurityScreen.js';
import { securityVariantElement } from '../screens/security-variants.js';
import { DashboardScreen } from '../screens/DashboardScreen.js';
import { TransactionsScreen } from '../screens/TransactionsScreen.js';
import { TransactionInformationScreen } from '../screens/TransactionInformationScreen.js';
import { TermLoanInformationScreen } from '../screens/TermLoanInformationScreen.js';
import { AccountInformationScreen } from '../screens/AccountInformationScreen.js';
import { DepositAccountScreen } from '../screens/DepositAccountScreen.js';
import { AccountHoldsScreen } from '../screens/AccountHoldsScreen.js';
import { AccountHistoryScreen } from '../screens/AccountHistoryScreen.js';
import { CardMaintenanceScreen } from '../screens/CardMaintenanceScreen.js';
import { CallHistoryScreen } from '../screens/CallHistoryScreen.js';
import { BillPaymentsScreen } from '../screens/BillPaymentsScreen.js';
import { FundsTransferScreen } from '../screens/FundsTransferScreen.js';
import { StandingOrdersScreen } from '../screens/StandingOrdersScreen.js';
import { DirectDebitsScreen } from '../screens/DirectDebitsScreen.js';
import { StatementsScreen } from '../screens/StatementsScreen.js';
import { ChequebookScreen } from '../screens/ChequebookScreen.js';
import { InternetPasswordScreen } from '../screens/InternetPasswordScreen.js';
import { ResetInternetPasswordScreen } from '../screens/ResetInternetPasswordScreen.js';
import { BrokerMediumSecurityScreen } from '../screens/BrokerMediumSecurityScreen.js';
import { MobileUpdateScreen } from '../screens/MobileUpdateScreen.js';
import { PersonalDetailsScreen } from '../screens/PersonalDetailsScreen.js';
import { NoticeInformationScreen } from '../screens/NoticeInformationScreen.js';
import { CustomerNotesScreen } from '../screens/CustomerNotesScreen.js';
import { PostalAddressScreen } from '../screens/PostalAddressScreen.js';
import { Open24RegistrationScreen } from '../screens/Open24RegistrationScreen.js';
import { Business24RegistrationScreen } from '../screens/Business24RegistrationScreen.js';
import { VisaRegistrationScreen } from '../screens/VisaRegistrationScreen.js';
import { MortgageRegistrationScreen } from '../screens/MortgageRegistrationScreen.js';
import { Broker24RegistrationScreen } from '../screens/Broker24RegistrationScreen.js';
import { AdminPortalScreen } from '../screens/AdminPortalScreen.js';

/**
 * Permission-gated route registry, keyed by screen id.
 *
 * Each entry's `id` corresponds to a `screen` permission enforced by the RBAC
 * engine on the server and mirrored client-side for navigation gating.
 */
export interface ScreenDefinition {
  /** Screen id; must match the RBAC screen permission id. */
  readonly id: string;
  /** Route path. */
  readonly path: string;
  /** Human-readable nav title. */
  readonly title: string;
  /** Screen component. */
  readonly element: React.JSX.Element;
}

export const screenRegistry: readonly ScreenDefinition[] = [
  {
    // SCRN 1 — ID&V (Customer Verification). The RBAC permission id is the
    // zero-padded screen code from the matrix (see rbac/data/screens.ts).
    id: 'SCRN-01',
    path: '/idv',
    title: 'ID&V',
    element: <IdvScreen />,
  },
  {
    // SCRN 2 — Customer Search.
    id: 'SCRN-02',
    path: '/customer-search',
    title: 'Customer Search',
    element: <CustomerSearchScreen />,
  },
  {
    // SCRN 3 — Medium Security (identity checks + 2FA OTP).
    id: 'SCRN-03',
    path: '/security/medium',
    title: 'Medium Security',
    element: <MediumSecurityScreen />,
  },
  {
    // SCRN 4 — VISA Medium Security (CCF-53).
    id: 'SCRN-04',
    path: '/security/visa-medium',
    title: 'VISA Medium Security',
    element: securityVariantElement('SCRN-04'),
  },
  {
    // SCRN 5 — BUS24 Medium Security (CCF-23).
    id: 'SCRN-05',
    path: '/security/bus24-medium',
    title: 'Business Medium Security',
    element: securityVariantElement('SCRN-05'),
  },
  {
    // SCRN 6 — Authorised VISA User (CCF-25).
    id: 'SCRN-06',
    path: '/security/visa-authorised-user',
    title: 'Authorised VISA User',
    element: securityVariantElement('SCRN-06'),
  },
  {
    // SCRN 7 — EDS High Security (CCF-26).
    id: 'SCRN-07',
    path: '/security/eds-high',
    title: 'EDS High Security',
    element: securityVariantElement('SCRN-07'),
  },
  {
    // SCRN 8 — VISA High Security (CCF-5).
    id: 'SCRN-08',
    path: '/security/visa-high',
    title: 'VISA High Security',
    element: securityVariantElement('SCRN-08'),
  },
  {
    // SCRN 9 — Mortgage Security (CCF-24).
    id: 'SCRN-09',
    path: '/security/mortgage',
    title: 'Mortgage Security',
    element: securityVariantElement('SCRN-09'),
  },
  {
    // SCRN 10 — BUS24 High Security (CCF-42).
    id: 'SCRN-10',
    path: '/security/bus24-high',
    title: 'Business High Security',
    element: securityVariantElement('SCRN-10'),
  },
  {
    // SCRN 11 — Outbound Security (CCF-105). Outbound-call scripting + security:
    // the agent places an outbound call (Genesys-native queue/number selection),
    // follows the regulatory scripting, and confirms the customer's DoB before
    // discussing the account. Further account actions proceed to full ID&V
    // (SCRN-01) → Medium/High Security, reusing the existing /idv/* + /security/*
    // validation flow — no new validation routes.
    id: 'SCRN-11',
    path: '/outbound-security',
    title: 'Outbound Security',
    element: <OutboundSecurityScreen />,
  },
  {
    // SCRN 54 — ECL Medium Security (CCF-106). The ECSA authenticates a
    // vulnerable customer with a Medium-Risk check: name + DoB + 2FA OTP, with a
    // fallback (landline / email / active Visa debit card; 1 correct passes)
    // when the customer cannot complete 2FA. SIBLING of the Medium/High security
    // screens — reuses the existing /security/otp/* + /security/questions routes;
    // no new validation routes. ECL-group screen (RBAC: ECLAgent + supervisory).
    id: 'SCRN-54',
    path: '/security/ecl-medium',
    title: 'ECL Medium Security',
    element: <EclMediumSecurityScreen />,
  },
  {
    // SCRN 55 — ECL High Security (CCF-107). The ECSA authenticates a
    // vulnerable, HIGH-risk customer with the full ECL identity suite (a
    // SUPERSET of ECL Medium): name + DoB + mother's maiden name + mobile +
    // recurring payments + joint accounts, plus 2FA OTP, with a fallback
    // (landline / email / active Visa debit card) whose pass threshold is 1
    // when the mobile is on file + confirmed, otherwise 2. SIBLING of the
    // Medium/High security screens — reuses the existing /security/otp/* +
    // /security/questions routes; no new validation routes. ECL-group screen
    // (RBAC: ECLAgent + supervisory).
    id: 'SCRN-55',
    path: '/security/ecl-high',
    title: 'ECL High Security',
    element: <EclHighSecurityScreen />,
  },
  {
    // SCRN 13 — Call History (CCF-3). QA/Support Chat-facing interaction journey
    // record. Read-only; loads via the Teller-ID-gated, getCallHistory-gated
    // BFF route (not verification-gated — review of records, not servicing data).
    id: 'SCRN-13',
    path: '/call-history',
    title: 'Call History',
    element: <CallHistoryScreen />,
  },
  {
    // SCRN 17 — Open24 Registration (EDS personal customers). Reached after a
    // deregistration when the online-card type is OPEN24 (CCF-8/CCF-46).
    id: 'SCRN-17',
    path: '/registration/open24',
    title: 'Open24 Registration',
    element: <Open24RegistrationScreen />,
  },
  {
    // SCRN 18 — Business24 Registration (CCF-57).
    id: 'SCRN-18',
    path: '/registration/business24',
    title: 'Business 24 Registration',
    element: <Business24RegistrationScreen />,
  },
  {
    // SCRN 19 — VISA Registration (CCF-58).
    id: 'SCRN-19',
    path: '/registration/visa',
    title: 'VISA Registration',
    element: <VisaRegistrationScreen />,
  },
  {
    // SCRN 20 — Mortgage Registration (CCF-59).
    id: 'SCRN-20',
    path: '/registration/mortgage',
    title: 'Mortgage Registration',
    element: <MortgageRegistrationScreen />,
  },
  {
    // SCRN 21 — Broker24 Registration (CCF-60).
    id: 'SCRN-21',
    path: '/registration/broker24',
    title: 'Broker 24 Registration',
    element: <Broker24RegistrationScreen />,
  },
  {
    // SCRN 22 — Internet Password (CCF-17): temporary internet password
    // generation (transfer-to-self-serve + create-manual-password).
    id: 'SCRN-22',
    path: '/internet-password',
    title: 'Internet Password',
    element: <InternetPasswordScreen />,
  },
  {
    // SCRN 57 — Reset internet password (CCF-318 / #425). The destination a
    // broker lands on after PASSING Broker 24 medium security: a broker login
    // is not linked to accounts (the dashboard would be blank), so the agent is
    // routed here to reset the broker's internet password instead of the
    // dashboard. Reuses the RBAC- + verification-gated `POST
    // /api/security/internet-password` (→ gateway `password/reset`) route; no
    // new gateway path. Also offered from every medium-security screen's
    // cross-navigation dropdown (SecurityProcessMenu).
    id: 'SCRN-57',
    path: '/reset-internet-password',
    title: 'Reset Internet Password',
    element: <ResetInternetPasswordScreen />,
  },
  {
    // SCRN 58 — Broker Medium Security (CCF-318 / #425). The Broker 24
    // MEDIUM-security verification screen. Per @dylan219's 2026-07-21 ruling,
    // the medium-security process menu links HERE (complete broker security
    // first); on a PASS the screen brings the agent directly to Reset Internet
    // Password (SCRN-57), NOT the customer dashboard (a broker login is not
    // account-linked). Reuses the RBAC-gated `POST /api/security/questions`
    // data route (sourced from `/customer/identification` + `/customer/info`) —
    // no new gateway path.
    id: 'SCRN-58',
    path: '/security/broker-medium',
    title: 'Broker Security',
    element: <BrokerMediumSecurityScreen />,
  },
  {
    // SCRN 24 — Personal details: address / email / landline (CCF-33/34).
    id: 'SCRN-24',
    path: '/personal-details',
    title: 'Change Customer Details',
    element: <PersonalDetailsScreen />,
  },
  {
    // SCRN 26 — Change Mobile Number (CCF-6): floor-support gate → new mobile →
    // read-back → confirm → 24-hour processing notice.
    id: 'SCRN-26',
    path: '/mobile-update',
    title: 'Mobile Update',
    element: <MobileUpdateScreen />,
  },
  {
    // SCRN 28 — Bill Payment (Payee) Overview (CCF-64 listing + CCF-66 detail).
    // The agent picks Bill Payments → Bill payment overview → account; lists
    // the account's bill payments and, on View/Amend, shows the beneficiary
    // detail panel. Reached from the dashboard "Payments and transfers" link.
    id: 'SCRN-28',
    path: '/payments',
    title: 'Bill Payments',
    element: <BillPaymentsScreen />,
  },
  {
    // SCRN 32 — Funds Transfer → non-notice internal transfer (CCF-69). The
    // agent picks "Transfer between own accounts", a From + To eligible account
    // and an amount, confirms, and completes. Reached from the Payments area.
    // The notice-account / force-withdrawal variant (CCF-70) has no R4 endpoint
    // (account/notice/penalty is absent) and renders an honest "not yet
    // available" note.
    id: 'SCRN-32',
    path: '/funds-transfer',
    title: 'Funds Transfer',
    element: <FundsTransferScreen />,
  },
  {
    // SCRN 37 — Standing Orders → Create (CCF-14 regular + CCF-73 VISA). The
    // overview/view-amend branches (SCRN-35/36) are not yet built. Reached from
    // the Payments screen's "Standing orders" payment option.
    id: 'SCRN-37',
    path: '/standing-orders',
    title: 'Standing Orders',
    element: <StandingOrdersScreen />,
  },
  {
    // SCRN 33 — Direct Debits Overview (CCF-71) + SCRN 34 — Direct Debit
    // Enquiry / Refusal / Cancel (CCF-71 / CCF-102). Reached from the Payments
    // screen's "Direct Debits" payment option. FRAMEWORK build (issue #64): the
    // PTSB R4 gateway Direct-Debit endpoints (the DD detail + refusal-write per
    // CCF-71 and the DD cancellation per CCF-102) are not yet contracted, so the
    // screen is wired UI-only (stubbed toasts, no BFF/banking calls) per the
    // Figma — swapped for the generated typed-client calls when the contract
    // lands. SCRN-33/34 are already in the RBAC matrix, so the route is RBAC-
    // gated by `canViewScreen` like every other screen.
    id: 'SCRN-33',
    path: '/direct-debits',
    title: 'Direct Debits',
    element: <DirectDebitsScreen />,
  },
  {
    // SCRN 38 — Statements Menu (CCF-28) + SCRN 39 — Up to date statement order
    // (CCF-74). Reached from the dashboard "Statements" quick link / process
    // menu. The duplicate / amend / interest-certificate sub-flows (CCF-75/29/38)
    // have no R4 gateway endpoint and are not built (they render an honest
    // "not yet available" note).
    id: 'SCRN-38',
    path: '/statements',
    title: 'Statements',
    element: <StatementsScreen />,
  },
  {
    // SCRN 40 — Chequebook Menu (CCF-36) + SCRN 41 — Chequebook Order / Stop a
    // Cheque (CCF-37 / CCF-76 / CCF-78). Reached from the dashboard / process
    // menu "Chequebook(s)" link. FRAMEWORK build (issue #53): the PTSB R4
    // gateway chequebook endpoints are not yet contracted, so the screen is
    // wired UI-only (stubbed toasts, no BFF/banking calls) per the Figma —
    // swapped for the generated typed-client calls when the contract lands. The
    // SCRN-40 screen permission is already in the RBAC matrix, so the route is
    // RBAC-gated by `canViewScreen` like every other screen.
    id: 'SCRN-40',
    path: '/chequebook',
    title: 'Chequebook',
    element: <ChequebookScreen />,
  },
  {
    // SCRN 23 — Personal details: view, amend and delete postal address
    // (CCF-30 view & delete + CCF-31 vulnerable-customer add/update).
    // Non-mortgage accounts only; reached from the dashboard process menu
    // ("Postal Address Information").
    id: 'SCRN-23',
    path: '/postal-address',
    title: 'Postal Address',
    element: <PostalAddressScreen />,
  },
  {
    // SCRN 51 — Notice Information (View / Apply / Delete) — CCF-90/91/92.
    // Notice/deposit accounts only; reached from the dashboard process menu
    // ("Notice Information").
    id: 'SCRN-51',
    path: '/notice-information',
    title: 'Notice Information',
    element: <NoticeInformationScreen />,
  },
  {
    // SCRN 14 — Customer Notes & Remarks (View / Add / Delete temporary
    // remarks) — CCF-50/56/51. Reached from the dashboard process menu
    // ("Customer Notes").
    id: 'SCRN-14',
    path: '/customer-notes',
    title: 'Customer Notes',
    element: <CustomerNotesScreen />,
  },
  {
    // SCRN 43 — Customer Dashboard (account tiles + quick links).
    id: 'SCRN-43',
    path: '/dashboard',
    title: 'Customer Dashboard',
    element: <DashboardScreen />,
  },
  {
    // SCRN 46 — Transactions (historic listing + account/option selector).
    id: 'SCRN-46',
    path: '/transactions',
    title: 'Transactions',
    element: <TransactionsScreen />,
  },
  {
    // SCRN 47 — Transaction Information (drill-down detail incl. SEPA block).
    id: 'SCRN-47',
    path: '/transaction-information',
    title: 'Transaction Information',
    element: <TransactionInformationScreen />,
  },
  {
    // SCRN 48 — Pending Transactions. Rendered by the same Transactions screen
    // (the "Pending" option of SCRN-46); this entry registers the RBAC screen
    // id and a direct route to the pending view.
    id: 'SCRN-48',
    path: '/transactions/pending',
    title: 'Pending Transactions',
    element: <TransactionsScreen />,
  },
  {
    // SCRN 42 — View Account Holds and Comments (CCF-79).
    id: 'SCRN-42',
    path: '/accounts/holds',
    title: 'Account Holds',
    element: <AccountHoldsScreen />,
  },
  {
    // SCRN 44 — Term Loan Information (CCF-110).
    id: 'SCRN-44',
    path: '/accounts/term-loan',
    title: 'Term Loan Information',
    element: <TermLoanInformationScreen />,
  },
  {
    // SCRN 45 — Account Information drill-down, current/savings (CCF-111).
    id: 'SCRN-45',
    path: '/accounts/information',
    title: 'Account Information',
    element: <AccountInformationScreen />,
  },
  {
    // SCRN 52 — Deposit Account drill-down (CCF-109).
    id: 'SCRN-52',
    path: '/accounts/deposit',
    title: 'Deposit Account Information',
    element: <DepositAccountScreen />,
  },
  {
    // SCRN 53 — Account History (CCF-103). Read-only servicing-history table for
    // a selected account. The PTSB R4 gateway has no dedicated account/history
    // op yet (blocked on PTSB UBAG-721/GMS-986/AGW-39); the BFF serves the
    // history from already-available account data behind the same gates.
    id: 'SCRN-53',
    path: '/accounts/history',
    title: 'Account History',
    element: <AccountHistoryScreen />,
  },
  {
    // SCRN 49 — Card Maintenance Menu (CCF-87) + SCRN 50 actions (CCF-19/20/88/89/77).
    // Rendered by the same screen: the menu drives the maintenance options.
    id: 'SCRN-49',
    path: '/cards',
    title: 'Card Maintenance',
    element: <CardMaintenanceScreen />,
  },
  {
    // SCRN 50 — PIN Reminder / Activate / Replace (rendered by SCRN-49's screen,
    // selected via the maintenance-option dropdown). Registers the RBAC id.
    id: 'SCRN-50',
    path: '/cards/actions',
    title: 'Card Maintenance Actions',
    element: <CardMaintenanceScreen />,
  },
  {
    // SCRN 56 — Admin Console (CCF-113 / #302). Admin + Supervisor only
    // (server-side RBAC — see rbac/data/permission-matrix.ts); every other
    // role is refused by the same `canViewScreen` gate every other screen
    // uses (renders PermissionDeniedPage, App.tsx).
    id: 'SCRN-56',
    path: '/admin',
    title: 'Admin Console',
    element: <AdminPortalScreen />,
  },
];
